import { useEffect, useState } from 'react'
import type { AgentOutputProps } from '../types'
import { apiClient } from '@/services/api'

interface ArgusRecording {
  id: string
  target_agent?: string | null
  prompt?: string | null
  target_url?: string | null
  duration_s?: number | null
  frame_count?: number | null
  fps?: number | null
  status?: string | null
  error?: string | null
  video_url: string
  metadata?: {
    steps?: Array<Record<string, any>>
    step_errors?: Array<Record<string, any>>
  } | null
  created_at?: string | null
}

function pickRecordingId(props: AgentOutputProps): string | null {
  const r = props.result ?? {}
  const ad = r.agent_data ?? {}
  const rr = ad.record_result ?? r.record_result ?? {}
  const fromResult =
    rr.recording_id ?? ad.argus_recording_id ?? r.argus_recording_id ?? null
  return (
    props.execution?.argus_recording_id ||
    fromResult ||
    (props.extra?.recording_id as string) ||
    null
  )
}

export default function ArgusSessionRenderer(props: AgentOutputProps) {
  const recordingId = pickRecordingId(props)
  const [rec, setRec] = useState<ArgusRecording | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!recordingId) return
    let cancelled = false
    setLoading(true)
    setErr(null)
    ;(apiClient as any).client
      .get(`/api/v1/argus/recordings/${recordingId}`)
      .then((resp: any) => {
        if (!cancelled) setRec(resp.data)
      })
      .catch((e: any) => {
        if (!cancelled) setErr(e?.message || 'failed to load recording')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [recordingId])

  if (!recordingId) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No Argus recording linked to this execution.
      </div>
    )
  }
  if (loading && !rec) {
    return <div className="text-sm text-muted-foreground">Loading recording…</div>
  }
  if (err && !rec) {
    return <div className="text-sm text-destructive">Failed to load recording: {err}</div>
  }
  if (!rec) return null

  const stepErrors = rec.metadata?.step_errors || []
  const steps = rec.metadata?.steps || []

  return (
    <div className="space-y-3">
      <video
        src={rec.video_url}
        controls
        className="w-full max-w-3xl rounded-lg border border-border bg-black"
      />

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {rec.target_agent && <span>Observing: <b>{rec.target_agent}</b></span>}
        {typeof rec.duration_s === 'number' && <span>{rec.duration_s.toFixed(1)}s</span>}
        {typeof rec.frame_count === 'number' && <span>{rec.frame_count} frames @ {rec.fps}fps</span>}
        {rec.target_url && <span className="truncate max-w-[320px]">{rec.target_url}</span>}
      </div>

      {rec.prompt && (
        <div className="text-xs">
          <div className="text-muted-foreground mb-1">Prompt submitted:</div>
          <pre className="bg-muted/40 border border-border rounded p-2 whitespace-pre-wrap break-words">
            {rec.prompt}
          </pre>
        </div>
      )}

      {stepErrors.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-destructive">
            {stepErrors.length} step error{stepErrors.length === 1 ? '' : 's'}
          </summary>
          <pre className="mt-1 bg-muted/40 border border-border rounded p-2 overflow-auto">
            {JSON.stringify(stepErrors, null, 2)}
          </pre>
        </details>
      )}

      {steps.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            {steps.length} recorded step{steps.length === 1 ? '' : 's'}
          </summary>
          <pre className="mt-1 bg-muted/40 border border-border rounded p-2 overflow-auto">
            {JSON.stringify(steps, null, 2)}
          </pre>
        </details>
      )}
    </div>
  )
}
